import { inject, Injectable } from '@angular/core';
import type {
  AgentConnectorMcpServerRef,
  AgentConnectorTool,
  ConnectorMcpServer,
  ConnectorRequirementDefinition,
} from './connector.models';
import { ConnectorApiService } from './connector-api.service';

const AGENT_DISPATCH_TOOL = 'run_sub_agent';

@Injectable({ providedIn: 'root' })
export class AgentConnectorRequirementsService {
  private readonly api = inject(ConnectorApiService);

  async resolve(agentIdentifier: string): Promise<ConnectorRequirementDefinition[]> {
    const identifier = canonicalAgentIdentifier(agentIdentifier);
    if (!identifier) return [];

    const parent = await this.api.getAgentDefinition(identifier);
    const teamIdentifiers = directTeamIdentifiers(parent.tools);
    const team = await Promise.all(
      teamIdentifiers.map((teamIdentifier) =>
        this.api.getAgentDefinition(teamIdentifier)
      )
    );
    const references = mergeMcpReferences([
      ...parent.mcp_servers,
      ...team.flatMap((agent) => agent.mcp_servers),
    ]);
    const servers = await Promise.all(
      references.map((reference) => this.resolveServer(reference))
    );

    return servers.flatMap((server) =>
      server?.transport_type === 'connector_oauth'
        ? [
            {
              mcpServerId: server.server_id,
              displayLabel: server.display_name || server.name || server.server_id,
              transportType: 'connector_oauth' as const,
              configuredProvider: server.connector_provider,
            },
          ]
        : []
    );
  }

  private async resolveServer(
    reference: AgentConnectorMcpServerRef
  ): Promise<ConnectorMcpServer | undefined> {
    if (!reference.server_id) return undefined;
    if (reference.transport_type && reference.display_name) {
      return {
        server_id: reference.server_id,
        name: reference.name ?? reference.server_name ?? reference.server_id,
        display_name: reference.display_name,
        transport_type: reference.transport_type,
        connector_provider: reference.connector_provider ?? null,
      };
    }
    return this.api.getMcpServer(reference.server_id);
  }
}

function canonicalAgentIdentifier(identifier: string): string {
  const trimmed = identifier.trim();
  if (!trimmed) return '';
  return trimmed.split(':').length === 2 ? `${trimmed}:default` : trimmed;
}

function baseAgentIdentifier(identifier: string): string {
  return canonicalAgentIdentifier(identifier).split(':').slice(0, 2).join(':');
}

function directTeamIdentifiers(tools: AgentConnectorTool[]): string[] {
  const identifiers = new Map<string, string>();
  for (const tool of tools) {
    const fixed = tool.tool_config?.['agent'];
    if (typeof fixed !== 'string' || !fixed.trim()) continue;
    const identifier = canonicalAgentIdentifier(fixed);
    identifiers.set(baseAgentIdentifier(identifier), identifier);
  }
  for (const tool of tools) {
    if ((tool.tool_name ?? tool.name) !== AGENT_DISPATCH_TOOL) continue;
    const entries = tool.tool_config?.['agents'];
    if (!Array.isArray(entries)) continue;
    for (const entry of entries) {
      if (!isRecord(entry) || typeof entry['identifier'] !== 'string') continue;
      const identifier = canonicalAgentIdentifier(entry['identifier']);
      if (!identifier) continue;
      identifiers.set(baseAgentIdentifier(identifier), identifier);
    }
  }
  return [...identifiers.values()];
}

function mergeMcpReferences(
  references: AgentConnectorMcpServerRef[]
): AgentConnectorMcpServerRef[] {
  const merged = new Map<string, AgentConnectorMcpServerRef>();
  for (const reference of references) {
    const key = reference.server_id ?? reference.name ?? reference.server_name;
    if (!key) continue;
    const current = merged.get(key);
    merged.set(
      key,
      current
        ? {
            server_id: reference.server_id ?? current.server_id,
            name: reference.name ?? current.name,
            display_name: reference.display_name ?? current.display_name,
            server_name: reference.server_name ?? current.server_name,
            transport_type: reference.transport_type ?? current.transport_type,
            connector_provider:
              reference.connector_provider ?? current.connector_provider,
          }
        : reference
    );
  }
  return [...merged.values()];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
